import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { baseURL, urls } from 'src/utils/api';
import store from 'src/store';
import { setUserAuthenticated } from 'src/slices/auth';

const protectedAxios = axios.create({ baseURL });

async function getFreshAccess(refreshToken: string) {
  const response = await protectedAxios.post<{
    refresh: string;
    access: string;
  }>(urls.refreshAccess, { refresh: refreshToken })
    .catch(() => {
      store.dispatch(setUserAuthenticated({ authenticated: false }));

      const errorToReturn = new Error('Время сессии истекло. Пожалуйста, войдите заново.');

      return Promise.reject(errorToReturn);
    });

  const {
    refresh: newRefreshToken,
    access: newAccessToken,
  } = response.data;

  return [newAccessToken, newRefreshToken];
}

function setSession(accessToken: string | null, refreshToken: string | null): void {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    protectedAxios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete protectedAxios.defaults.headers.common.Authorization;
  }

  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  } else {
    localStorage.removeItem('refreshToken');
  }
}

function isValidToken(accessToken: string): boolean {
  if (!accessToken) {
    return false;
  }

  const decoded: any = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  const tokenExpires = decoded.exp - 30;

  return tokenExpires > currentTime;
}

function handle401(
  error: { config?: any, response?: any, request?: any, status: number }
): Promise<any> {
  if (
    error.config
    && error.response
    && error.response.status === 401
  ) {
    if (error.response.data.code !== 'token_not_valid') {
      store.dispatch(setUserAuthenticated({ authenticated: false }));
    }

    // Отказ на сам запрос обновления не обновляется повторно, иначе цикл:
    // сервер на просроченный refresh отвечает тем же token_not_valid.
    // Сравнивается адрес запроса, а не responseURL: тот всегда полный, а
    // baseURL бывает относительным (стенд собран с REACT_APP_API_URL=""), и
    // прежнее сравнение никогда не срабатывало — 26.10.2026 стенд получил
    // 170 тысяч запросов обновления подряд, вход был невозможен.
    if (
      error.response.data.code === 'token_not_valid'
      && error.config.url !== urls.refreshAccess
    ) {
      const refreshToken = window.localStorage.getItem('refreshToken');

      return getFreshAccess(`${refreshToken}`)
        .then(([newAccessToken, newRefreshToken]) => {
          setSession(newAccessToken, newRefreshToken);

          const config = {
            ...error.config,
            headers: {
              common: {
                Authorization: `Bearer ${newAccessToken}`
              }
            }
          };

          const { data } = error.config;

          if (data) {
            try {
              config.data = JSON.parse(data);
            } catch (e) {
              console.warn();
            }
          }

          return protectedAxios.request(config);
        });
    }
  }

  return Promise.reject(error);
}

protectedAxios.interceptors.response.use(
  null,
  handle401
);

export {
  protectedAxios,
  isValidToken,
  setSession,
  getFreshAccess,
  handle401
};
